import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const AuthModal = () => {
  const { authOpen, setAuthOpen, authTab, setAuthTab, login, signup, checkoutPlan } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const done = (msg) => {
    toast.success(msg);
    setAuthOpen(false);
    setErr("");
    setF({ name: "", email: "", password: "" });
    if (!checkoutPlan) nav("/dashboard");
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!f.email || !f.password) throw new Error("Please enter your login and password.");
      const u = await login({ email: f.email, password: f.password });
      done(`Welcome back, ${u.name.split(" ")[0]}.`);
    } catch (x) { setErr(x.message); }
    setBusy(false);
  };

  const onSignup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!f.name.trim()) throw new Error("Please enter your name.");
      if (!/^\S+@\S+\.\S+$/.test(f.email)) throw new Error("Please enter a valid email.");
      if (f.password.length < 6) throw new Error("Password must be 6+ characters.");
      if (!agree) throw new Error("Please accept the no-refund policy.");
      await signup({ ...f, agree });
      done("Account created.");
    } catch (x) { setErr(x.message); }
    setBusy(false);
  };

  const switchTab = (t) => { setAuthTab(t); setErr(""); };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="gwap-modal" data-testid="auth-modal">
        <DialogTitle className="modal-title">{authTab === "login" ? "Welcome Back" : "Create Your Account"}</DialogTitle>
        <DialogDescription className="sr-only">Login or create a GWAP The Charts account</DialogDescription>
        {checkoutPlan && (
          <div className="modal-plan-note">Selected plan: <b>{checkoutPlan.name} — ${checkoutPlan.price}</b>. Create an account or log in to continue to checkout.</div>
        )}
        <div className="modal-tabs">
          <button className={`modal-tab ${authTab === "login" ? "active" : ""}`} onClick={() => switchTab("login")} data-testid="tab-login">Login</button>
          <button className={`modal-tab ${authTab === "create" ? "active" : ""}`} onClick={() => switchTab("create")} data-testid="tab-create">Create Account</button>
        </div>

        {authTab === "login" ? (
          <form onSubmit={onLogin}>
            <label className="mlbl">Email or Username</label>
            <input className="minp" type="text" autoComplete="username" placeholder="your@email.com" value={f.email} onChange={set("email")} data-testid="login-email" />
            <label className="mlbl">Password</label>
            <input className="minp" type="password" placeholder="Your password" value={f.password} onChange={set("password")} data-testid="login-password" />
            <div className="merr" data-testid="auth-error">{err}</div>
            <button className="mbtn" type="submit" disabled={busy} data-testid="login-submit">Login →</button>
          </form>
        ) : (
          <form onSubmit={onSignup}>
            <label className="mlbl">Full Name</label>
            <input className="minp" type="text" placeholder="Your name" value={f.name} onChange={set("name")} data-testid="signup-name" />
            <label className="mlbl">Email</label>
            <input className="minp" type="email" placeholder="your@email.com" value={f.email} onChange={set("email")} data-testid="signup-email" />
            <label className="mlbl">Password</label>
            <input className="minp" type="password" placeholder="Create a password (6+ chars)" value={f.password} onChange={set("password")} data-testid="signup-password" />
            <div className="agree">
              <Checkbox id="refundAgree" checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="agree-box" data-testid="signup-agree" />
              <label htmlFor="refundAgree">I understand all sales are final and <strong>no refunds will be issued</strong> for any reason.</label>
            </div>
            <div className="merr" data-testid="auth-error">{err}</div>
            <button className="mbtn" type="submit" disabled={busy} data-testid="signup-submit">Create Account →</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
